import type { AccessLevel } from './accessTypes';

/**
 * Helpers for the flat permission codes the platform actually enforces on.
 *
 * The portal continues to gate on codes through `usePermissions()`; this module only builds the *new*
 * code family the access model introduces, so a component can ask "may this user export customers?"
 * without hand-assembling a string.
 *
 * The projection itself — which codes a level emits for a domain — lives in the backend and is
 * deliberately NOT duplicated here (adr-merchant-portal-013). The mapping is irregular (`catalog.*` for
 * products but `catalog.structure.*` for dictionaries, several domains spanning multiple namespaces), and
 * a second copy would drift from the one that grants access.
 */

/** Separator marking the canonical capability-code family, e.g. `customers.feature.export`. */
const FEATURE_SEGMENT = 'feature';

/**
 * The canonical permission code for a capability within a domain.
 *
 * Note the backend also emits a *legacy alias* for capabilities that predate the model
 * (`settings.payments.credentials.manage` alongside `payments.feature.credentials`). Both are permanent.
 * Gate new affordances on this canonical form; existing checks keep working on their legacy code.
 */
export function featureCode(domainCode: string, capabilityCode: string): string {
  return `${domainCode}.${FEATURE_SEGMENT}.${capabilityCode}`;
}

/** True when a permission code belongs to the canonical capability family. */
export function isFeatureCode(code: string): boolean {
  return code.includes(`.${FEATURE_SEGMENT}.`);
}

/** i18n key for a domain's display name (namespace `access`). Falls back to the raw code — see below. */
export function domainLabelKey(domainCode: string): string {
  return `domains.${domainCode}.label`;
}

/** i18n key for a domain's one-line description. */
export function domainDescriptionKey(domainCode: string): string {
  return `domains.${domainCode}.description`;
}

/**
 * i18n key for a capability's display name.
 *
 * Keyed by capability alone, not by `<domain>.<capability>`: the same code means the same thing wherever
 * it appears — `export` is "Экспорт" in Customers and in Catalog alike — so scoping the key per domain
 * would mean maintaining the same translation twenty times and letting them drift.
 */
export function capabilityLabelKey(capabilityCode: string): string {
  return `capabilities.${capabilityCode}.label`;
}

/** i18n key for the explanation of why a capability is separated out (its risk class). */
export function riskLabelKey(risk: string): string {
  return `risks.${risk}.label`;
}

/** i18n key for an access level's display name. */
export function levelLabelKey(level: AccessLevel): string {
  return `levels.${level}.label`;
}

/** i18n key for the plain-language explanation of what a level permits. */
export function levelDescriptionKey(level: AccessLevel): string {
  return `levels.${level}.description`;
}

/** i18n key for a UI group heading (the navigation sections the catalogue mirrors). */
export function groupLabelKey(group: string): string {
  return `groups.${group}.label`;
}
